import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { AlertTriangle, Ban, Check, Paperclip, Plus, Save } from 'lucide-react';
import {
  RECEIVING_DIVERGENCE_STATUS_LABELS,
  RECEIVING_DIVERGENCE_TYPES,
  RECEIVING_DIVERGENCE_TYPE_LABELS,
  RECEIVING_ITEM_CONDITIONS,
  RECEIVING_ITEM_CONDITION_LABELS,
  RECEIVING_SITUATION_LABELS,
  SERVICE_ACCEPTANCES,
  SERVICE_ACCEPTANCE_LABELS,
  formatCents,
  formatQty,
  type Receiving,
  type ReceivingDivergence,
  type ReceivingItem,
} from '@amai/contracts';
import { api } from '@/lib/api';
import { errorMessage } from '@/lib/errors';
import { blank, formatDate, formatDocument } from '@/lib/format';
import { T } from '@/design/tokens';
import { WorkspaceHeader } from '@/components/WorkspaceHeader';
import { Attachments } from '@/components/Attachments';
import { ReadField } from '@/components/ContextPanel';
import { StatusBadge } from '@/components/StatusBadge';
import { Button } from '@/components/Button';
import { ConfirmDialog } from '@/components/Modal';
import { Field, FormSection } from '@/components/FormSection';
import { QtyInput } from '@/components/MoneyInput';
import { ErrorState, LoadingState } from '@/components/states';
import { useAuth } from '@/lib/auth-context';
import { useStockLocations } from '@/lib/queries';
import { DIVERGENCE_TONE, LISTA_RECEBIMENTOS, SITUATION_TONE } from './recebimentos-shared';
import { DateInput } from '@/components/DateInput';

interface LinhaDraft {
  delivered: number | null;
  accepted: number | null;
  condition: string;
  serviceStatus: string;
  notes: string;
  excessJustification: string;
  batchCode: string;
  batchExpiry: string;
}

function draftOf(item: ReceivingItem): LinhaDraft {
  return {
    delivered: item.deliveredQuantityMilli,
    accepted: item.acceptedQuantityMilli,
    condition: item.condition,
    serviceStatus: item.serviceStatus ?? '',
    notes: item.notes ?? '',
    excessJustification: '',
    batchCode: item.batchCode ?? '',
    batchExpiry: item.batchExpiry ?? '',
  };
}

/**
 * Conferência do recebimento — página própria, não modal.
 *
 * Um pedido de 30 linhas conferido num drawer é sofrimento em notebook e
 * impossível em tablet. Aqui cada linha tem espaço para entregue × aceita ×
 * recusada, a situação, a observação e as fotos. Salvar guarda a conferência
 * sem tocar no estoque; concluir é o ato que dá entrada.
 */
export function RecebimentoConferenciaPage({ id }: { id: string }) {
  const { can } = useAuth();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const locations = useStockLocations();

  const query = useQuery({
    queryKey: ['receiving', id],
    queryFn: () => api.get<Receiving>(`/receivings/${id}`),
  });
  const receiving = query.data;

  const [carregado, setCarregado] = useState<string | null>(null);
  const [linhas, setLinhas] = useState<Record<string, LinhaDraft>>({});
  const [locationId, setLocationId] = useState('');
  const [receivedAt, setReceivedAt] = useState('');
  const [documentNumber, setDocumentNumber] = useState('');
  const [documentKey, setDocumentKey] = useState('');
  const [notes, setNotes] = useState('');
  const [anexosAbertos, setAnexosAbertos] = useState<Record<string, boolean>>({});
  const [confirmando, setConfirmando] = useState<'conclude' | 'cancel' | null>(null);
  const [novaDivergencia, setNovaDivergencia] = useState(false);

  // Carrega o rascunho UMA vez por versão do recebimento: o que o usuário
  // digita não é sobrescrito por um refetch no meio da conferência.
  if (receiving && carregado !== receiving.id) {
    setCarregado(receiving.id);
    setLinhas(Object.fromEntries(receiving.items.map((item) => [item.purchaseOrderItemId, draftOf(item)])));
    setLocationId(receiving.locationId);
    setReceivedAt(receiving.receivedAt.slice(0, 10));
    setDocumentNumber(receiving.documentNumber ?? '');
    setDocumentKey(receiving.documentKey ?? '');
    setNotes(receiving.notes ?? '');
  }

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['receivings'] });
    void queryClient.invalidateQueries({ queryKey: ['receiving', id] });
    void queryClient.invalidateQueries({ queryKey: ['purchase-orders'] });
    void queryClient.invalidateQueries({ queryKey: ['purchase-order'] });
    void queryClient.invalidateQueries({ queryKey: ['stock-balances'] });
    void queryClient.invalidateQueries({ queryKey: ['stock-movements'] });
  };

  const aberto = receiving && (receiving.status === 'RASCUNHO' || receiving.status === 'EM_CONFERENCIA');
  const podeEditar = Boolean(aberto) && can('recebimentos:editar');
  const podeExcesso = can('recebimentos:excesso');

  const corpo = () => ({
    locationId: locationId || undefined,
    receivedAt: receivedAt ? new Date(`${receivedAt}T12:00:00`).toISOString() : undefined,
    documentNumber: documentNumber.trim() || null,
    documentKey: documentKey.trim() || null,
    notes: notes.trim() || null,
    items: (receiving?.items ?? []).map((item) => {
      const d = linhas[item.purchaseOrderItemId] ?? draftOf(item);
      const delivered = d.delivered ?? 0;
      const recusaServico = d.serviceStatus === 'RECUSADO' || d.serviceStatus === 'AGUARDANDO_CORRECAO';
      const accepted = recusaServico ? 0 : Math.min(d.accepted ?? delivered, delivered);
      return {
        purchaseOrderItemId: item.purchaseOrderItemId,
        deliveredQuantityMilli: delivered,
        acceptedQuantityMilli: accepted,
        rejectedQuantityMilli: delivered - accepted,
        condition: d.condition || undefined,
        serviceStatus: item.kind === 'SERVICO' && d.serviceStatus ? d.serviceStatus : undefined,
        notes: d.notes.trim() || null,
        excessJustification: d.excessJustification.trim() || null,
        // Só a linha de item que controla lote manda os dois campos: o resto
        // do pedido não tem lote nenhum para informar.
        batchCode: item.tracksBatch ? d.batchCode.trim() || null : null,
        batchExpiry: item.tracksBatch ? d.batchExpiry || null : null,
      };
    }),
  });

  const salvar = useMutation({
    mutationFn: () => api.patch<Receiving>(`/receivings/${id}`, corpo()),
    onSuccess: () => {
      setCarregado(null);
      refresh();
    },
  });
  const concluir = useMutation({
    mutationFn: async () => {
      await api.patch<Receiving>(`/receivings/${id}`, corpo());
      return api.post<Receiving>(`/receivings/${id}/conclude`, {});
    },
    onSuccess: () => {
      setConfirmando(null);
      refresh();
      void navigate({ to: LISTA_RECEBIMENTOS });
    },
  });
  const cancelar = useMutation({
    mutationFn: (reason: string) => api.post<Receiving>(`/receivings/${id}/cancel`, { reason }),
    onSuccess: () => {
      setConfirmando(null);
      refresh();
      void navigate({ to: LISTA_RECEBIMENTOS });
    },
  });

  // O que impede concluir AGORA — mostrado antes de o servidor recusar.
  const problemas: string[] = [];
  if (receiving) {
    let algumaConferida = false;
    for (const item of receiving.items) {
      const d = linhas[item.purchaseOrderItemId];
      if (!d) continue;
      const delivered = d.delivered ?? 0;
      const recusaServico = d.serviceStatus === 'RECUSADO' || d.serviceStatus === 'AGUARDANDO_CORRECAO';
      const accepted = recusaServico ? 0 : (d.accepted ?? delivered);
      if (delivered > 0 || (item.kind === 'SERVICO' && d.serviceStatus)) algumaConferida = true;
      if (accepted > delivered) problemas.push(`"${item.description}": aceita maior que a entregue.`);
      if (accepted > item.expectedQuantityMilli) {
        if (!podeExcesso) {
          problemas.push(
            `"${item.description}": chegou mais que o pedido. Aceite até ${formatQty(item.expectedQuantityMilli)} ${item.unit} e recuse o resto, ou peça a quem pode aceitar excesso.`,
          );
        } else if (d.excessJustification.trim().length < 10) {
          problemas.push(`"${item.description}": explique por que o excesso está sendo aceito (mínimo 10 caracteres).`);
        }
      }
    }
    if (!algumaConferida) problemas.push('Informe o que chegou em pelo menos uma linha.');
  }

  if (query.isPending) return <LoadingState label="Abrindo a conferência…" />;
  if (query.isError) return <ErrorState message={errorMessage(query.error)} onRetry={() => void query.refetch()} />;
  if (!receiving) return null;

  return (
    <>
      <WorkspaceHeader
        back={{ to: LISTA_RECEBIMENTOS, label: 'Recebimentos' }}
        title={`Conferência ${receiving.code}`}
        meta={[
          { label: 'Pedido', value: receiving.purchaseOrderCode ?? '—' },
          {
            label: 'Fornecedor',
            value: `${receiving.supplierName ?? '—'}${receiving.supplierDocument ? ` · ${formatDocument(receiving.supplierDocument)}` : ''}`,
          },
        ]}
        actions={
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {podeEditar && (
              <>
                <Button variant="ghost" icon={Ban} onClick={() => setConfirmando('cancel')}>
                  Cancelar conferência
                </Button>
                <Button variant="outline" icon={Save} loading={salvar.isPending} onClick={() => salvar.mutate()}>
                  Salvar
                </Button>
                {can('recebimentos:concluir') && (
                  <Button icon={Check} disabled={problemas.length > 0} onClick={() => setConfirmando('conclude')}>
                    Concluir recebimento
                  </Button>
                )}
              </>
            )}
          </div>
        }
      />

      <div className="workspace__body">
        {!aberto && (
          <div
            className="card"
            style={{ padding: '12px 16px', marginBottom: 16, display: 'flex', gap: 10, alignItems: 'center' }}
          >
            <StatusBadge kind={SITUATION_TONE[receiving.situation]}>
              {RECEIVING_SITUATION_LABELS[receiving.situation]}
            </StatusBadge>
            <span style={{ fontSize: 13 }}>
              Este recebimento não está mais em conferência. O que está abaixo é o registro do que foi conferido.
            </span>
          </div>
        )}

        {(salvar.isError || concluir.isError) && (
          <div className="login__alert" role="alert" style={{ marginBottom: 14 }}>
            {errorMessage(salvar.error ?? concluir.error)}
          </div>
        )}
        {salvar.isSuccess && !salvar.isPending && (
          <div className="card" style={{ padding: '10px 14px', marginBottom: 14, borderColor: T.green, background: T.greenTint, fontSize: 12.5, color: T.green }}>
            Conferência salva. Nada entrou no estoque ainda — isso acontece ao concluir.
          </div>
        )}

        <div className="grid-2" style={{ alignItems: 'start' }}>
          <FormSection title="Dados do recebimento">
            <div className="grid-2">
              <Field label="Local de recebimento" htmlFor="conf-local">
                <select
                  id="conf-local"
                  className="input"
                  value={locationId}
                  disabled={!podeEditar}
                  onChange={(e) => setLocationId(e.target.value)}
                >
                  {(locations.data ?? []).map((l) => (
                    <option key={l.id} value={l.id}>
                      {l.code} · {l.name}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Data do recebimento" htmlFor="conf-data">
                <DateInput
                  id="conf-data"
                  className="input"
                  value={receivedAt}
                  disabled={!podeEditar}
                  onChange={(iso) => setReceivedAt(iso)}
                />
              </Field>
            </div>
            <div className="grid-2">
              <Field label="NF-e / DANFE que acompanhou" htmlFor="conf-nf" hint="Opcional. O XML entra em Notas de entrada.">
                <input
                  id="conf-nf"
                  className="input"
                  value={documentNumber}
                  disabled={!podeEditar}
                  onChange={(e) => setDocumentNumber(e.target.value)}
                  placeholder="432689"
                />
              </Field>
              <Field label="Chave de acesso" htmlFor="conf-chave" hint="44 dígitos, se estiver na DANFE.">
                <input
                  id="conf-chave"
                  className="input mono"
                  value={documentKey}
                  disabled={!podeEditar}
                  onChange={(e) => setDocumentKey(e.target.value)}
                  placeholder="3526 0904 5128 8700 …"
                />
              </Field>
            </div>
            <Field label="Observações" htmlFor="conf-notes">
              <textarea
                id="conf-notes"
                className="input"
                rows={2}
                value={notes}
                disabled={!podeEditar}
                onChange={(e) => setNotes(e.target.value)}
              />
            </Field>
          </FormSection>

          <FormSection title="Origem">
            <ReadField label="Pedido" value={blank(receiving.purchaseOrderCode)} mono />
            <ReadField label="Requisição" value={blank(receiving.requisitionCode)} mono />
            <ReadField label="Cotação" value={blank(receiving.quotationCode)} mono />
            <ReadField label="Centro de custo" value={blank(receiving.costCenterName)} />
            <ReadField label="Conferente" value={blank(receiving.receivedByName)} />
            <ReadField label="Aberto em" value={formatDate(receiving.createdAt)} mono />
            <div style={{ marginTop: 8 }}>
              <div className="muted" style={{ fontSize: 11.5, marginBottom: 6 }}>
                Documentos do recebimento (DANFE, canhoto, termo)
              </div>
              <Attachments entity="Receiving" entityId={receiving.id} canEdit={podeEditar} />
            </div>
          </FormSection>
        </div>

        <FormSection title="Conferência item a item">
          <p className="muted" style={{ fontSize: 12.5, marginTop: -4, marginBottom: 12, lineHeight: 1.6 }}>
            <strong>Entregue</strong> é o que o fornecedor trouxe; <strong>aceita</strong> é o que passou na conferência;
            a diferença é <strong>recusada</strong>. Só a aceita entra no estoque. O que faltar continua pendente no pedido.
          </p>

          {receiving.items.map((item) => {
            const d = linhas[item.purchaseOrderItemId] ?? draftOf(item);
            const delivered = d.delivered ?? 0;
            const recusaServico = d.serviceStatus === 'RECUSADO' || d.serviceStatus === 'AGUARDANDO_CORRECAO';
            const accepted = recusaServico ? 0 : (d.accepted ?? delivered);
            const rejected = Math.max(delivered - accepted, 0);
            const excesso = accepted > item.expectedQuantityMilli;
            const falta = delivered < item.expectedQuantityMilli;
            const setLinha = (patch: Partial<LinhaDraft>) =>
              setLinhas((atual) => ({ ...atual, [item.purchaseOrderItemId]: { ...(atual[item.purchaseOrderItemId] ?? draftOf(item)), ...patch } }));
            const anexos = anexosAbertos[item.id] ?? false;

            return (
              <div key={item.id} className="card" style={{ padding: 14, marginBottom: 12 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
                  <div>
                    <div style={{ fontWeight: 600, fontSize: 13.5 }}>
                      {item.description}
                      {item.kind === 'SERVICO' && (
                        <StatusBadge kind="info">
                          Serviço
                        </StatusBadge>
                      )}
                    </div>
                    {(item.specification || item.brand || item.model) && (
                      <div className="muted" style={{ fontSize: 12, marginTop: 2 }}>
                        {[item.specification, item.brand, item.model].filter(Boolean).join(' · ')}
                      </div>
                    )}
                    <div className="mono" style={{ fontSize: 12, color: T.inkFaint, marginTop: 4 }}>
                      pedido {formatQty(item.orderedQuantityMilli)} {item.unit} · já recebido{' '}
                      {formatQty(item.previouslyReceivedMilli)} · <strong>saldo esperado {formatQty(item.expectedQuantityMilli)}</strong>
                      {item.sku ? ` · ${item.sku}` : ''}
                    </div>
                  </div>
                  <div className="mono" style={{ fontSize: 12.5, textAlign: 'right' }}>
                    <div>custo {formatCents(item.unitCostCents)}</div>
                    <div style={{ fontWeight: 600 }}>{formatCents(Math.round((accepted * item.unitCostCents) / 1000))}</div>
                  </div>
                </div>

                <div className="grid-4" style={{ marginTop: 12 }}>
                  <Field label="Entregue" htmlFor={`ent-${item.id}`}>
                    <QtyInput
                      id={`ent-${item.id}`}
                      unit={item.unit}
                      value={d.delivered}
                      disabled={!podeEditar}
                      onChange={(v) => {
                        const nova = v ?? 0;
                        setLinha({
                          delivered: v,
                          // Aceita acompanha a entregue enquanto o conferente não separou a recusa.
                          accepted: d.accepted === null || d.accepted === delivered ? v : Math.min(d.accepted, nova),
                        });
                      }}
                    />
                  </Field>
                  <Field label="Aceita" htmlFor={`ace-${item.id}`}>
                    <QtyInput
                      id={`ace-${item.id}`}
                      unit={item.unit}
                      value={d.accepted}
                      disabled={!podeEditar}
                      invalid={accepted > delivered}
                      onChange={(v) => setLinha({ accepted: v })}
                    />
                  </Field>
                  <Field label="Recusada" htmlFor={`rec-${item.id}`}>
                    <input id={`rec-${item.id}`} className="input mono" value={`${formatQty(rejected)} ${item.unit}`} readOnly />
                  </Field>
                  {item.kind === 'SERVICO' ? (
                    <Field label="Aceite do serviço" htmlFor={`srv-${item.id}`}>
                      <select
                        id={`srv-${item.id}`}
                        className="input"
                        value={d.serviceStatus}
                        disabled={!podeEditar}
                        onChange={(e) => {
                          const status = e.target.value;
                          // Recusado ou aguardando correção não é aceite: a
                          // quantidade aceita vai a zero junto, para a tela
                          // não mostrar um valor que o servidor vai zerar.
                          const recusa = status === 'RECUSADO' || status === 'AGUARDANDO_CORRECAO';
                          setLinha({ serviceStatus: status, ...(recusa ? { accepted: 0 } : {}) });
                        }}
                      >
                        <option value="">—</option>
                        {SERVICE_ACCEPTANCES.map((s) => (
                          <option key={s} value={s}>
                            {SERVICE_ACCEPTANCE_LABELS[s]}
                          </option>
                        ))}
                      </select>
                    </Field>
                  ) : (
                    <Field label="Situação" htmlFor={`cond-${item.id}`}>
                      <select
                        id={`cond-${item.id}`}
                        className="input"
                        value={d.condition}
                        disabled={!podeEditar}
                        onChange={(e) => setLinha({ condition: e.target.value })}
                      >
                        {RECEIVING_ITEM_CONDITIONS.map((c) => (
                          <option key={c} value={c}>
                            {RECEIVING_ITEM_CONDITION_LABELS[c]}
                          </option>
                        ))}
                      </select>
                    </Field>
                  )}
                </div>

                {excesso && (
                  <div
                    style={{
                      marginTop: 8,
                      padding: '10px 12px',
                      borderRadius: 8,
                      border: `1px solid ${T.amberLine}`,
                      background: T.amberTint,
                      fontSize: 12.5,
                    }}
                  >
                    <div style={{ display: 'flex', gap: 6, color: T.amber, alignItems: 'center' }}>
                      <AlertTriangle size={14} aria-hidden />
                      <strong>Excesso de {formatQty(accepted - item.expectedQuantityMilli)} {item.unit}</strong> além do saldo do pedido.
                    </div>
                    {podeExcesso ? (
                      <Field label="Por que o excesso está sendo aceito?" htmlFor={`exc-${item.id}`}>
                        <input
                          id={`exc-${item.id}`}
                          className="input"
                          value={d.excessJustification}
                          disabled={!podeEditar}
                          onChange={(e) => setLinha({ excessJustification: e.target.value })}
                          placeholder="Bonificação do fornecedor, aceita pela Diretoria"
                        />
                      </Field>
                    ) : (
                      <div style={{ marginTop: 4 }}>
                        Você não tem permissão para aceitar excesso. Aceite até {formatQty(item.expectedQuantityMilli)}{' '}
                        {item.unit} e recuse o restante, ou peça a quem pode.
                      </div>
                    )}
                  </div>
                )}
                {falta && delivered > 0 && d.condition === 'OK' && (
                  <div className="muted" style={{ fontSize: 12, marginTop: 6 }}>
                    Faltam {formatQty(item.expectedQuantityMilli - delivered)} {item.unit}: continuam pendentes no pedido. Se
                    for falta de verdade (não entrega combinada), marque a situação como &quot;Falta&quot;.
                  </div>
                )}

                {item.tracksBatch && (
                  <div className="grid-2" style={{ marginTop: 8 }}>
                    <Field
                      label="Lote"
                      htmlFor={`lote-${item.id}`}
                      hint="Da embalagem que chegou — sem ele o recebimento não conclui"
                    >
                      <input
                        id={`lote-${item.id}`}
                        className="input input--mono"
                        value={d.batchCode}
                        disabled={!podeEditar}
                        onChange={(e) => setLinha({ batchCode: e.target.value })}
                        placeholder="L2026A"
                      />
                    </Field>
                    <Field label="Validade" htmlFor={`val-${item.id}`}>
                      <DateInput
                        id={`val-${item.id}`}
                        className="input input--mono"
                        value={d.batchExpiry}
                        disabled={!podeEditar}
                        onChange={(iso) => setLinha({ batchExpiry: iso })}
                      />
                    </Field>
                  </div>
                )}

                <div className="grid-2" style={{ marginTop: 8, alignItems: 'end' }}>
                  <Field label="Observação" htmlFor={`obs-${item.id}`}>
                    <input
                      id={`obs-${item.id}`}
                      className="input"
                      value={d.notes}
                      disabled={!podeEditar}
                      onChange={(e) => setLinha({ notes: e.target.value })}
                      placeholder="Ex.: 5 sacos rasgados na carroceria"
                    />
                  </Field>
                  <div style={{ paddingBottom: 2 }}>
                    <Button
                      variant="ghost"
                      icon={Paperclip}
                      onClick={() => setAnexosAbertos((a) => ({ ...a, [item.id]: !anexos }))}
                    >
                      {anexos ? 'Ocultar fotos e anexos' : 'Fotos e anexos da linha'}
                    </Button>
                  </div>
                </div>
                {anexos && (
                  <div style={{ marginTop: 8 }}>
                    <Attachments entity="ReceivingItem" entityId={item.id} canEdit={podeEditar} />
                  </div>
                )}
              </div>
            );
          })}

          {problemas.length > 0 && podeEditar && (
            <div className="card" style={{ padding: '10px 14px', borderColor: T.amberLine, background: T.amberTint, fontSize: 12.5 }}>
              <strong style={{ color: T.amber }}>Antes de concluir:</strong>
              <ul style={{ margin: '4px 0 0', paddingLeft: 18 }}>
                {problemas.map((p) => (
                  <li key={p}>{p}</li>
                ))}
              </ul>
            </div>
          )}
        </FormSection>

        <Divergencias
          receiving={receiving}
          podeEditar={can('recebimentos:editar') && receiving.status !== 'CANCELADO'}
          abrindo={novaDivergencia}
          onAbrir={() => setNovaDivergencia(true)}
          onFechar={() => setNovaDivergencia(false)}
          onChanged={() => {
            setNovaDivergencia(false);
            refresh();
          }}
        />
      </div>

      <ConfirmDialog
        open={confirmando !== null}
        title={confirmando === 'conclude' ? 'Concluir o recebimento' : 'Cancelar a conferência'}
        message={
          confirmando === 'conclude'
            ? 'A quantidade ACEITA de cada linha entra no estoque agora, o pedido abate o recebido e a requisição vira atendida. Depois disso, desfazer é estorno.'
            : 'A conferência é descartada e o pedido volta a aceitar um recebimento novo. Nada entrou no estoque.'
        }
        confirmLabel={confirmando === 'conclude' ? 'Concluir e dar entrada' : 'Cancelar conferência'}
        destructive={confirmando === 'cancel'}
        requireReason={confirmando === 'cancel'}
        loading={concluir.isPending || cancelar.isPending}
        error={
          confirmando === 'conclude'
            ? concluir.error
              ? errorMessage(concluir.error)
              : undefined
            : cancelar.error
              ? errorMessage(cancelar.error)
              : undefined
        }
        onClose={() => setConfirmando(null)}
        onConfirm={(reason) => {
          if (confirmando === 'conclude') concluir.mutate();
          else cancelar.mutate(reason ?? '');
        }}
      />
    </>
  );
}

/* ------------------------------ divergências ------------------------------ */

function Divergencias({
  receiving,
  podeEditar,
  abrindo,
  onAbrir,
  onFechar,
  onChanged,
}: {
  receiving: Receiving;
  podeEditar: boolean;
  abrindo: boolean;
  onAbrir: () => void;
  onFechar: () => void;
  onChanged: () => void;
}) {
  const [type, setType] = useState<string>('AVARIA');
  const [receivingItemId, setReceivingItemId] = useState('');
  const [quantity, setQuantity] = useState<number | null>(null);
  const [description, setDescription] = useState('');

  const criar = useMutation({
    mutationFn: () =>
      api.post(`/receivings/${receiving.id}/divergences`, {
        receivingItemId: receivingItemId || null,
        type,
        quantityMilli: quantity ?? 0,
        description: description.trim(),
      }),
    onSuccess: () => {
      setDescription('');
      setQuantity(null);
      onChanged();
    },
  });

  return (
    <FormSection title="Divergências">
      {receiving.divergences.length === 0 && !abrindo && (
        <p className="muted" style={{ fontSize: 12.5, marginTop: -4 }}>
          Nenhuma registrada. Falta, excesso e recusa marcados nas linhas viram divergência ao concluir; o que não cabe
          numa linha (embalagem violada, nota errada) registra-se aqui.
        </p>
      )}

      {receiving.divergences.map((d) => (
        <DivergenciaCard key={d.id} receivingId={receiving.id} divergence={d} podeEditar={podeEditar} onChanged={onChanged} />
      ))}

      {podeEditar && !abrindo && (
        <Button variant="outline" icon={Plus} onClick={onAbrir}>
          Registrar divergência
        </Button>
      )}

      {abrindo && (
        <div className="card" style={{ padding: 14 }}>
          {criar.isError && (
            <div className="login__alert" role="alert" style={{ marginBottom: 10 }}>
              {errorMessage(criar.error)}
            </div>
          )}
          <div className="grid-3">
            <Field label="Tipo" htmlFor="div-tipo">
              <select id="div-tipo" className="input" value={type} onChange={(e) => setType(e.target.value)}>
                {RECEIVING_DIVERGENCE_TYPES.map((t) => (
                  <option key={t} value={t}>
                    {RECEIVING_DIVERGENCE_TYPE_LABELS[t]}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Linha (opcional)" htmlFor="div-linha">
              <select id="div-linha" className="input" value={receivingItemId} onChange={(e) => setReceivingItemId(e.target.value)}>
                <option value="">Recebimento inteiro</option>
                {receiving.items.map((i) => (
                  <option key={i.id} value={i.id}>
                    {i.description}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Quantidade afetada" htmlFor="div-qtd">
              <QtyInput id="div-qtd" value={quantity} onChange={setQuantity} />
            </Field>
          </div>
          <Field label="Descrição" htmlFor="div-desc">
            <textarea
              id="div-desc"
              className="input"
              rows={2}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Ex.: embalagem violada em 3 caixas; conteúdo conferido, sem falta"
            />
          </Field>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <Button variant="ghost" onClick={onFechar}>
              Voltar
            </Button>
            <Button disabled={description.trim().length < 3} loading={criar.isPending} onClick={() => criar.mutate()}>
              Registrar
            </Button>
          </div>
        </div>
      )}
    </FormSection>
  );
}

function DivergenciaCard({
  receivingId,
  divergence,
  podeEditar,
  onChanged,
}: {
  receivingId: string;
  divergence: ReceivingDivergence;
  podeEditar: boolean;
  onChanged: () => void;
}) {
  const [status, setStatus] = useState<string>(divergence.status);
  const [resolution, setResolution] = useState('');
  const [anexos, setAnexos] = useState(false);
  const fechada = ['RESOLVIDA', 'ACEITA', 'RECUSADA'].includes(divergence.status);
  const precisaResolucao = ['RESOLVIDA', 'ACEITA', 'RECUSADA'].includes(status);

  const atualizar = useMutation({
    mutationFn: () =>
      api.patch(`/receivings/${receivingId}/divergences/${divergence.id}`, {
        status,
        resolution: resolution.trim() || null,
      }),
    onSuccess: onChanged,
  });

  return (
    <div className="card" style={{ padding: 12, marginBottom: 10 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
        <div>
          <strong style={{ fontSize: 13 }}>{RECEIVING_DIVERGENCE_TYPE_LABELS[divergence.type]}</strong>
          {divergence.itemDescription && (
            <span className="muted" style={{ fontSize: 12 }}>
              {' '}
              · {divergence.itemDescription}
            </span>
          )}
          {divergence.quantityMilli > 0 && (
            <span className="mono muted" style={{ fontSize: 12 }}>
              {' '}
              · {formatQty(divergence.quantityMilli)}
            </span>
          )}
        </div>
        <StatusBadge kind={DIVERGENCE_TONE[divergence.status]}>
          {RECEIVING_DIVERGENCE_STATUS_LABELS[divergence.status]}
        </StatusBadge>
      </div>
      <div style={{ fontSize: 12.5, marginTop: 6 }}>{divergence.description}</div>
      {divergence.resolution && (
        <div style={{ fontSize: 12, marginTop: 6, color: T.green }}>
          Tratamento: {divergence.resolution}
          {divergence.resolvedByName ? ` — ${divergence.resolvedByName}` : ''}
        </div>
      )}

      {podeEditar && !fechada && (
        <div className="grid-3" style={{ marginTop: 10, alignItems: 'end' }}>
          <Field label="Tratamento" htmlFor={`dv-st-${divergence.id}`}>
            <select id={`dv-st-${divergence.id}`} className="input" value={status} onChange={(e) => setStatus(e.target.value)}>
              {(['ABERTA', 'EM_ANALISE', 'RESOLVIDA', 'ACEITA', 'RECUSADA'] as const).map((s) => (
                <option key={s} value={s}>
                  {RECEIVING_DIVERGENCE_STATUS_LABELS[s]}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Como foi tratada" htmlFor={`dv-res-${divergence.id}`}>
            <input
              id={`dv-res-${divergence.id}`}
              className="input"
              value={resolution}
              onChange={(e) => setResolution(e.target.value)}
              placeholder="Fornecedor vai repor 5 unidades"
            />
          </Field>
          <div style={{ paddingBottom: 2, display: 'flex', gap: 8 }}>
            <Button
              variant="outline"
              disabled={status === divergence.status && !resolution.trim()}
              loading={atualizar.isPending}
              onClick={() => atualizar.mutate()}
            >
              Aplicar
            </Button>
            <Button variant="ghost" icon={Paperclip} onClick={() => setAnexos((v) => !v)}>
              Fotos
            </Button>
          </div>
          {precisaResolucao && !resolution.trim() && (
            <div className="muted" style={{ fontSize: 11.5, gridColumn: '1 / -1' }}>
              Para fechar a divergência, registre como ela foi tratada.
            </div>
          )}
        </div>
      )}
      {atualizar.isError && (
        <div className="login__alert" role="alert" style={{ marginTop: 8 }}>
          {errorMessage(atualizar.error)}
        </div>
      )}
      {(anexos || fechada) && (
        <div style={{ marginTop: 8 }}>
          <Attachments entity="ReceivingDivergence" entityId={divergence.id} canEdit={podeEditar && !fechada} />
        </div>
      )}
    </div>
  );
}
